import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { paths } from '@/app/paths';
import { Icon, normalizeHex, sectionType, useToast } from '@/design-system';
import { useStructureLabels } from '@/features/structure/labels';
import { preferencesQuery, structureQuery } from '@/features/structure/api';
import { applyVariantState, variantsKey, type VariantState } from '@/features/variants/api';
import { formatTime } from '@/lib/format';
import { errorText } from '@/shell/errorText';
import { checkpoint, inspectorEdit, restoreRevision, revisionsKey, revisionsQuery, type ColorEdit, type RevisionOut } from './api';
import type { DesignerCtx } from './DesignerTab';
import s from './panels.module.css';

/** The side column: «СЕКЦИИ» with «Свойства · ‹section›», then «ВЕРСИИ». */
export function SidePanel({ ctx, onCompare }: { ctx: DesignerCtx; onCompare: (rev: RevisionOut) => void }) {
  return (
    <div className={s.side}>
      <SectionsCard ctx={ctx} />
      <VersionsCard ctx={ctx} onCompare={onCompare} />
    </div>
  );
}

/** Puts a move's answer into the cache and refreshes the variant and its revisions. */
function useApplyState(ctx: DesignerCtx) {
  const qc = useQueryClient();
  return (state: VariantState) => {
    applyVariantState(qc, ctx.site.id, state);
    void qc.invalidateQueries({ queryKey: variantsKey(ctx.site.id) });
    void qc.invalidateQueries({ queryKey: revisionsKey(ctx.variant.id) });
  };
}

/** Saves an inspector edit (P4-B08: deterministic, one revision per request) and refreshes the variant. */
function useInspector(ctx: DesignerCtx) {
  const { t } = useTranslation('designer');
  const { t: tc } = useTranslation('common');
  const toast = useToast();
  const apply = useApplyState(ctx);
  return (edit: ColorEdit) =>
    inspectorEdit(ctx.variant.id, [edit], ctx.variant.head?.id ?? null)
      .then((state) => {
        apply(state);
        toast(t('sections.edited', { n: state.head?.number ?? '' }));
      })
      .catch((e: unknown) => toast(errorText(e, tc)));
}

/**
 * «СЕКЦИИ»: the page's sections (the structure); the selected one is tinted and selects it in the
 * preview. «Свойства · ‹section›»: Фон and Акцент (colour inspector edits on the theme's `background` and
 * `accent` tokens), Анимация (the site's motion preference, read-only), Пресет (the section's pinned
 * design; opens it in the structure).
 */
function SectionsCard({ ctx }: { ctx: DesignerCtx }) {
  const { site, variant, selection, select } = ctx;
  const { t } = useTranslation('designer');
  const navigate = useNavigate();
  const { section, designName } = useStructureLabels();
  const secs = useQuery(structureQuery(site.id)).data?.sections ?? [];
  const edit = useInspector(ctx);
  const picked = selection.section;
  const bg = normalizeHex(variant.palette?.background) ?? '#FFFFFF';
  const acc = normalizeHex(variant.palette?.accent ?? variant.palette?.primary) ?? '#000000';
  // The API has no per-section animation (inspector edits are text, image and colour): the row shows the
  // site's motion preference, read-only (DESIGN_SPEC §4.5 «As built (P4-E04)»).
  const motion = useQuery(preferencesQuery(site.id)).data?.motion ?? 'auto';
  const pinned = secs.find((x) => x.section_type === picked)?.presets[0];
  const color = (token: 'background' | 'accent', value: string, current: string) => {
    if (value.toUpperCase() !== current.toUpperCase()) void edit({ kind: 'color', token, value: value.toUpperCase() });
  };
  return (
    <section className={s.sections} aria-label={t('sections.title')}>
      <span className={s.caps}>{t('sections.title')}</span>
      {secs.map((x) => (
        <button key={x.id} type="button" className={s.secItem} aria-current={picked === x.section_type} onClick={() => select({ section: x.section_type, el: null })}>
          <Icon name={sectionType(x.section_type).icon} size={14} />
          {section(x.section_type)}
        </button>
      ))}
      <div className={s.props}>
        <span className={s.propsTitle}>{t('sections.props', { section: picked ?? '—' })}</span>
        <div className={s.prop}>
          <span className={s.propLabel}>{t('sections.bg')}</span>
          <ColorValue value={bg} bordered label={t('sections.change_color', { name: t('sections.bg') })} onCommit={(v) => color('background', v, bg)} />
        </div>
        <div className={s.prop}>
          <span className={s.propLabel}>{t('sections.accent')}</span>
          <ColorValue value={acc} label={t('sections.change_color', { name: t('sections.accent') })} onCommit={(v) => color('accent', v, acc)} />
        </div>
        <div className={s.prop}>
          <span className={s.propLabel}>{t('sections.animation')}</span>
          <span title={t('sections.motion_hint')}>{t(`sections.motions.${motion}`)}</span>
        </div>
        <div className={s.prop}>
          <span className={s.propLabel}>{t('sections.preset')}</span>
          <button type="button" className={s.valueBtn} title={t('sections.preset_open')} onClick={() => navigate(paths.site(site.id, 'structure'))}>
            {pinned ? t('sections.preset_value', { name: designName(pinned), version: pinned.version }) : t('sections.preset_ai')}
          </button>
        </div>
      </div>
    </section>
  );
}

/** A swatch and its hex; a hidden colour input over it picks a new colour, committed once the PM stops (600 ms). */
function ColorValue({ value, bordered, label, onCommit }: { value: string; bordered?: boolean; label: string; onCommit: (v: string) => void }) {
  const [local, setLocal] = useState(value);
  const timer = useRef<number | undefined>(undefined);
  useEffect(() => setLocal(value), [value]);
  useEffect(() => () => window.clearTimeout(timer.current), []);
  return (
    <label className={s.swatchValue}>
      <span className={bordered ? s.swatchBordered : s.swatch} style={{ background: local }} />
      {local.toUpperCase()}
      <input
        type="color"
        className={s.colorInput}
        aria-label={label}
        value={local.toLowerCase()}
        onChange={(e) => {
          const v = e.target.value;
          setLocal(v);
          window.clearTimeout(timer.current);
          timer.current = window.setTimeout(() => onCommit(v), 600);
        }}
      />
    </label>
  );
}

/** «ВЕРСИИ»: «rN · message · who · time», the current one tinted «текущая»; restore; a past row compares (C5); «Checkpoint». */
function VersionsCard({ ctx, onCompare }: { ctx: DesignerCtx; onCompare: (rev: RevisionOut) => void }) {
  const { variant } = ctx;
  const { t } = useTranslation('designer');
  const { t: tc } = useTranslation('common');
  const toast = useToast();
  const revs = useQuery(revisionsQuery(variant.id)).data ?? [];
  const [pending, setPending] = useState(false);
  const head = variant.head;
  const apply = useApplyState(ctx);
  const restore = (r: RevisionOut) =>
    restoreRevision(r.id)
      .then((state) => {
        apply(state);
        toast(t('versions.restored', { n: r.number }));
      })
      .catch((e: unknown) => toast(errorText(e, tc)));
  const save = () => {
    if (!head || pending) return;
    setPending(true);
    checkpoint(head.id, t('versions.checkpoint_label', { n: head.number }))
      .then((state) => {
        apply(state);
        toast(t('versions.checkpoint_saved', { n: state.head?.number ?? head.number + 1, from: head.number }));
      })
      .catch((e: unknown) => toast(errorText(e, tc)))
      .finally(() => setPending(false));
  };
  return (
    <section className={s.versions} aria-label={t('versions.label')}>
      <div className={s.versionsHead}>
        <span className={s.capsInline}>{t('versions.title')}</span>
        <button type="button" className={s.checkpoint} onClick={save}>
          <Icon name="bookmark-plus" size={13} />
          {t('versions.checkpoint')}
        </button>
      </div>
      {revs.map((r) => {
        const cur = r.is_head || r.id === head?.id;
        return (
          // A past revision's row compares it with the current one (C5); the row keeps the design's layout.
          <div
            key={r.id}
            className={s.revRow}
            aria-current={cur}
            role={cur ? undefined : 'button'}
            tabIndex={cur ? undefined : 0}
            title={cur ? undefined : t('versions.compare')}
            aria-label={cur ? undefined : `${t('versions.compare')}: r${r.number}`}
            onClick={cur ? undefined : () => onCompare(r)}
            onKeyDown={cur ? undefined : (e) => (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), onCompare(r))}
          >
            <span className={s.revNo}>{t('versions.number', { n: r.number })}</span>
            <div className={s.revText}>
              <span className={s.revSummary}>{r.summary}</span>
              <span className={s.revMeta}>{t('versions.meta', { who: t(`versions.who.${r.author_kind}`), time: formatTime(r.created_at) })}</span>
            </div>
            {cur && <span className={s.revCurrent}>{t('versions.current')}</span>}
            <button
              type="button"
              className={s.revBtn}
              title={t('versions.restore')}
              aria-label={`${t('versions.restore')}: r${r.number}`}
              onClick={(e) => {
                e.stopPropagation();
                void restore(r);
              }}
              onKeyDown={(e) => e.stopPropagation()}
            >
              <Icon name="history" size={12} />
            </button>
          </div>
        );
      })}
    </section>
  );
}
